import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faShieldHalved,
} from "@awesome.me/kit-70c1f0055f/icons/classic/regular";
import { PanelEmptyState } from "@/components/mission-control/PanelEmptyState";
import type { Audit } from "@/services/api/guardiansv2/types";
import { AuditCard } from "./AuditCard";
import { buildAuditListEntries } from "./groupAuditsByMission";
import type { ScopeLabelCache } from "./useScopeLabelCache";

export interface AuditsPaneProps {
  audits: Audit[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  hasMore: boolean;
  onLoadMore: () => void;
  scrollRootRef: RefObject<HTMLElement | null>;
  scopeLabels: ScopeLabelCache;
  listClassName?: string;
}

/**
 * Audits list grouped by mission with infinite scroll.
 */
export function AuditsPane({
  audits,
  loading,
  loadingMore,
  error,
  selectedId,
  onSelect,
  hasMore,
  onLoadMore,
  scrollRootRef,
  scopeLabels,
  listClassName,
}: AuditsPaneProps) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [expandedMissions, setExpandedMissions] = useState<Set<string>>(() => new Set());

  const entries = useMemo(() => buildAuditListEntries(audits), [audits]);

  const toggleMission = useCallback((missionId: string) => {
    setExpandedMissions((prev) => {
      const next = new Set(prev);
      if (next.has(missionId)) {
        next.delete(missionId);
      } else {
        next.add(missionId);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const root = scrollRootRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) onLoadMore();
      },
      { root, rootMargin: "200px", threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore, scrollRootRef, audits.length]);

  if (error) {
    return (
      <p className="text-sm text-red-300" data-testid="audits-error">
        {error}
      </p>
    );
  }

  if (loading && audits.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-slate-400" data-testid="audits-loading">
        Loading…
      </p>
    );
  }

  if (!loading && audits.length === 0) {
    return (
      <PanelEmptyState
        icon={faShieldHalved}
        title="No audits"
        subtitle="Adjust filters or wait for the next agentic interaction to complete."
        testId="audits-empty"
      />
    );
  }

  return (
    <div className={clsx("space-y-5 p-1", listClassName)} data-testid="audits-pane">
      {entries.map((entry) => {
        if (entry.kind === "single") {
          return (
            <AuditCard
              key={entry.audit._id}
              audit={entry.audit}
              selected={selectedId === entry.audit._id}
              onClick={() => onSelect(entry.audit._id)}
            />
          );
        }

        const expanded = expandedMissions.has(entry.missionId);
        const hasMultiple = entry.audits.length > 1;
        const visibleAudits = expanded || !hasMultiple ? entry.audits : entry.audits.slice(0, 1);
        const headerLabel =
          scopeLabels.missionLabel(entry.missionId) ??
          `Mission ${entry.missionId.slice(0, 8)}…`;

        return (
          <section
            key={entry.missionId}
            data-testid={`audit-mission-group-${entry.missionId}`}
          >
            <div className="mb-2 flex items-center gap-1">
              {hasMultiple ? (
                <button
                  type="button"
                  onClick={() => toggleMission(entry.missionId)}
                  className="flex shrink-0 items-center justify-center rounded p-1 text-slate-400 hover:bg-slate-700/50 hover:text-slate-200"
                  aria-expanded={expanded}
                  aria-label={
                    expanded ? "Show only latest audit" : "Show all mission audits"
                  }
                  data-testid={`audit-mission-toggle-${entry.missionId}`}
                >
                  <FontAwesomeIcon
                    icon={expanded ? faChevronDown : faChevronRight}
                    className="h-3 w-3"
                  />
                </button>
              ) : (
                <span className="w-5 shrink-0" aria-hidden />
              )}
              <span
                className="min-w-0 flex-1 truncate text-left text-xs font-semibold uppercase tracking-wide text-slate-400"
                data-testid={`audit-mission-group-title-${entry.missionId}`}
              >
                {headerLabel}
                <span className="ml-2 font-normal text-slate-500">
                  ({entry.audits.length})
                </span>
              </span>
            </div>
            <div className="space-y-2 pl-5">
              {visibleAudits.map((audit) => (
                <AuditCard
                  key={audit._id}
                  audit={audit}
                  selected={selectedId === audit._id}
                  onClick={() => onSelect(audit._id)}
                />
              ))}
            </div>
          </section>
        );
      })}
      <div
        ref={sentinelRef}
        className="h-1 w-full shrink-0"
        aria-hidden
        data-testid="audits-scroll-sentinel"
      />
      {loadingMore ? (
        <p className="py-3 text-center text-sm text-slate-400" data-testid="audits-loading-more">
          Loading more…
        </p>
      ) : null}
    </div>
  );
}
